import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { RoleSummary } from '../../api/roles';
import { BranchSummary } from '../../api/branches';
import { UserListItem } from '../../types/user';
import {
  createUserFormSchema,
  CreateUserFormValues,
  updateUserFormSchema,
  UpdateUserFormValues,
} from '../../validation/user';

interface UserFormDialogProps {
  open: boolean;
  onClose: () => void;
  editingUser: UserListItem | null;
  roles: RoleSummary[];
  branches: BranchSummary[];
  onCreate: (values: CreateUserFormValues) => Promise<void>;
  onUpdate: (id: number, values: UpdateUserFormValues) => Promise<void>;
  errorMessage: string | null;
}

export const UserFormDialog = ({
  open,
  onClose,
  editingUser,
  roles,
  branches,
  onCreate,
  onUpdate,
  errorMessage,
}: UserFormDialogProps) => {
  const isEdit = !!editingUser;

  const form = useForm<CreateUserFormValues | UpdateUserFormValues>({
    resolver: zodResolver(isEdit ? updateUserFormSchema : createUserFormSchema),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (open) {
      reset(
        editingUser
          ? {
              firstName: editingUser.firstName,
              lastName: editingUser.lastName,
              phone: editingUser.phone ?? '',
              roleId: editingUser.role.id,
              branchId: editingUser.branch?.id,
            }
          : { firstName: '', lastName: '', email: '', phone: '', password: '', roleId: undefined },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingUser]);

  const onSubmit = async (values: CreateUserFormValues | UpdateUserFormValues) => {
    if (isEdit && editingUser) {
      await onUpdate(editingUser.id, values as UpdateUserFormValues);
    } else {
      await onCreate(values as CreateUserFormValues);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit user' : 'Create user'}</DialogTitle>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Stack direction="row" spacing={2}>
              <TextField
                label="First name"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                {...register('firstName')}
              />
              <TextField
                label="Last name"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                {...register('lastName')}
              />
            </Stack>

            {!isEdit && (
              <TextField
                label="Email"
                type="email"
                fullWidth
                error={!!(errors as Record<string, { message?: string }>).email}
                helperText={(errors as Record<string, { message?: string }>).email?.message}
                {...register('email' as keyof CreateUserFormValues)}
              />
            )}

            <TextField label="Phone" fullWidth {...register('phone')} />

            {!isEdit && (
              <TextField
                label="Password"
                type="password"
                fullWidth
                error={!!(errors as Record<string, { message?: string }>).password}
                helperText={(errors as Record<string, { message?: string }>).password?.message}
                {...register('password' as keyof CreateUserFormValues)}
              />
            )}

            <TextField
              select
              label="Role"
              fullWidth
              defaultValue={editingUser?.role.id ?? ''}
              error={!!errors.roleId}
              helperText={errors.roleId?.message}
              {...register('roleId')}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Branch"
              fullWidth
              defaultValue={editingUser?.branch?.id ?? ''}
              {...register('branchId')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
